import React, { useEffect, useState } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";
import { 
  Users, CreditCard, BarChart3, Settings, Shield, 
  LogOut, ChevronRight, TrendingUp, DollarSign, Building2, Activity,
  MessageSquare, Database, Lock, Menu, Tag, CalendarClock, Presentation, Percent, FileText, Zap, Rocket, Send, Nfc
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import AdminUsers from "./AdminUsers";
import AdminPlans from "./AdminPlans";
import AdminUsage from "./AdminUsage";
import AdminUsageBilling from "./AdminUsageBilling";
import AdminSettings from "./AdminSettings";
import AdminRevenue from "./AdminRevenue";
import AdminCommunications from "./AdminCommunications";
import AdminSecurity from "./AdminSecurity";
import AdminSystem from "./AdminSystem";
import AdminPromoCodes from "./AdminPromoCodes";
import AdminSubscriptions from "./AdminSubscriptions";
import AdminDemoRequests from "./AdminDemoRequests";
import AdminDiscounts from "./AdminDiscounts";
import AdminProposals from "./AdminProposals";
import AdminStripeConnect from "./AdminStripeConnect";
import AdminTapToPayPanel from "./AdminTapToPayPanel";
import AdminOnboarding from "./AdminOnboarding";
import AdminEmailMarketing from "./marketing/AdminEmailMarketing";
import AdminSupportAccessLog from "./AdminSupportAccessLog";

const AdminDashboard = () => {
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: "/admin", icon: BarChart3, label: "Overview", exact: true },
    { path: "/admin/users", icon: Users, label: "Users" },
    { path: "/admin/plans", icon: CreditCard, label: "Plans" },
    { path: "/admin/subscriptions", icon: CalendarClock, label: "Subscriptions" },
    { path: "/admin/promo-codes", icon: Tag, label: "Promo Codes" },
    { path: "/admin/discounts", icon: Percent, label: "Discounts" },
    { path: "/admin/usage", icon: Activity, label: "Usage" },
    { path: "/admin/usage-billing", icon: Zap, label: "Communication Usage" },
    { path: "/admin/revenue", icon: DollarSign, label: "Revenue" },
    { path: "/admin/stripe-connect", icon: Zap, label: "Stripe Connect" },
    { path: "/admin/tap-to-pay", icon: Nfc, label: "Tap to Pay" },
    { path: "/admin/communications", icon: MessageSquare, label: "Communications" },
    { path: "/admin/email-marketing", icon: Send, label: "Email Marketing" },
    { path: "/admin/proposals", icon: FileText, label: "Proposals" },
    { path: "/admin/onboarding", icon: Rocket, label: "Onboarding" },
    { path: "/admin/security", icon: Lock, label: "Security" },
    { path: "/admin/support-access", icon: Shield, label: "Support Access Log" },
    { path: "/admin/demo-requests", icon: Presentation, label: "Demo Requests" },
    { path: "/admin/system", icon: Database, label: "System" },
    { path: "/admin/settings", icon: Settings, label: "Settings" },
  ];

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setStats(res.data);
      setIsAdmin(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
        toast.error("Admin access required");
      } else {
        toast.error("Failed to load admin dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    document.cookie = "session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    window.location.href = "/login";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground">Loading...</div>
      </div>
    );
  }

  if (isAdmin === false) {
    return <Navigate to="/dashboard" replace />;
  }

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const AdminNav = () => (
    <>
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setMobileMenuOpen(false)}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              isActive(item.path, item.exact)
                ? "bg-red-500/20 text-red-400 border border-red-500/30"
                : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
            }`}
            data-testid={`admin-nav-${item.label.toLowerCase()}`}
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
            {isActive(item.path, item.exact) && <ChevronRight className="w-4 h-4 ml-auto" />}
          </Link>
        ))}
      </nav>

      <div className="p-4 border-t border-theme">
        <Link
          to="/dashboard"
          onClick={() => setMobileMenuOpen(false)}
          className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#0046BE]/20 text-[#0046BE] hover:bg-[#0046BE]/30 transition-all mb-2 border border-[#0046BE]/30"
          data-testid="back-to-dashboard-btn"
        >
          <Building2 className="w-5 h-5" />
          <span className="font-medium">Business Dashboard</span>
        </Link>
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all w-full"
          data-testid="admin-logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      {/* Sidebar - Desktop only */}
      <div className="hidden lg:flex w-64 bg-theme-surface backdrop-blur-xl border-r border-theme flex-col">
        <div className="p-6 border-b border-theme">
          <Link to="/admin" className="flex items-center justify-center">
            <img src="/logo.png" alt="Fieldz AI" className="h-10 object-contain" />
          </Link>
        </div>
        <AdminNav />
      </div>

      {/* Mobile Sidebar */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="left" className="w-64 p-0 bg-background border-theme">
          <div className="p-6 border-b border-theme">
            <Link to="/admin" className="flex items-center justify-center" onClick={() => setMobileMenuOpen(false)}>
              <img src="/logo.png" alt="Fieldz AI" className="h-10 object-contain" />
            </Link>
          </div>
          <div className="flex flex-col h-[calc(100%-81px)]">
            <AdminNav />
          </div>
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-40 flex items-center gap-3 px-4 py-3 bg-background/80 backdrop-blur-lg border-b border-theme">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted/50"
            data-testid="admin-mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-400" />
            <h1 className="text-base font-semibold text-foreground font-['Manrope']">Admin Panel</h1>
          </div>
        </div>

        <div className="p-4 lg:p-8">
          <Routes>
            <Route index element={<AdminOverview stats={stats} onRefresh={checkAdminAccess} />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="promo-codes" element={<AdminPromoCodes />} />
            <Route path="discounts" element={<AdminDiscounts />} />
            <Route path="usage" element={<AdminUsage />} />
            <Route path="usage-billing" element={<AdminUsageBilling />} />
            <Route path="revenue" element={<AdminRevenue />} />
            <Route path="stripe-connect" element={<AdminStripeConnect />} />
            <Route path="tap-to-pay" element={<AdminTapToPayPanel />} />
            <Route path="communications" element={<AdminCommunications />} />
            <Route path="email-marketing" element={<AdminEmailMarketing />} />
            <Route path="proposals" element={<AdminProposals />} />
            <Route path="onboarding" element={<AdminOnboarding />} />
            <Route path="security" element={<AdminSecurity />} />
            <Route path="support-access" element={<AdminSupportAccessLog />} />
            <Route path="demo-requests" element={<AdminDemoRequests />} />
            <Route path="system" element={<AdminSystem />} />
            <Route path="settings" element={<AdminSettings />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

// Admin Overview Component
const AdminOverview = ({ stats, onRefresh }) => {
  if (!stats) return null;

  const statCards = [
    { title: "Total Users", value: stats.total_users, icon: Users, color: "text-blue-400", bg: "bg-blue-500/10" },
    { title: "Active Users", value: stats.active_users, icon: Users, color: "text-green-400", bg: "bg-green-500/10" },
    { title: "Suspended", value: stats.suspended_users, icon: Users, color: "text-red-400", bg: "bg-red-500/10" },
    { title: "New (30d)", value: stats.recent_signups, icon: TrendingUp, color: "text-purple-400", bg: "bg-purple-500/10" },
    { title: "Total Jobs", value: stats.total_jobs?.toLocaleString(), icon: BarChart3, color: "text-cyan-400", bg: "bg-cyan-500/10" },
    { title: "Total Revenue", value: `$${(stats.total_revenue || 0).toLocaleString()}`, icon: DollarSign, color: "text-[#FFCC00]", bg: "bg-[#FFCC00]/10" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">Admin Dashboard</h1>
          <p className="text-muted-foreground">System overview and management</p>
        </div>
        <Button onClick={onRefresh} variant="outline" className="border-theme text-foreground hover:bg-muted/50">
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map((stat, i) => (
          <Card key={i} className="glass-card border-theme">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{stat.title}</p>
                  <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Users by Plan */}
      <Card className="glass-card border-theme">
        <CardHeader>
          <CardTitle className="text-foreground text-lg font-['Manrope']">Users by Subscription Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(stats.users_by_plan || {}).map(([plan, count]) => (
              <div key={plan} className="bg-muted/50 rounded-lg p-4 border border-theme">
                <div className="flex items-center justify-between">
                  <span className="text-foreground capitalize font-medium">{plan || "No Plan"}</span>
                  <Badge className={
                    plan === "business" ? "bg-purple-500/20 text-purple-400" :
                    plan === "professional" ? "bg-blue-500/20 text-blue-400" :
                    "bg-gray-500/20 text-gray-400"
                  }>
                    {count} users
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/admin/users">
          <Card className="glass-card border-theme hover:border-muted-foreground transition-all cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold">Manage Users</h3>
                <p className="text-muted-foreground text-sm">View, edit, suspend user accounts</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/plans">
          <Card className="glass-card border-theme hover:border-muted-foreground transition-all cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="text-foreground font-semibold">Subscription Plans</h3>
                <p className="text-muted-foreground text-sm">Create and manage pricing plans</p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
            </CardContent>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
